import { IBrowserAccessor } from '../browser';
import { expect } from '../expect';
import { IStorageStrategy } from './storage-strategy.interface';

export class NDaysStorageStrategy<T> implements IStorageStrategy<T> {
  private readonly browser: IBrowserAccessor;
  private readonly FT_3DA_STORAGE_LIFETIME: number;
  private readonly STORAGE_KEY: string;
  private readonly PREDICATE: () => boolean;

  constructor(browser: IBrowserAccessor, ftTtl: number, storageKey: string, predicate: (...any) => boolean) {
    this.browser = expect(browser).toBeDefined('The IBrowserAccessor implementation is required to construct DefaultStorageStrategy.');

    this.FT_3DA_STORAGE_LIFETIME = ftTtl;
    this.STORAGE_KEY = storageKey;
    this.PREDICATE = predicate;
  }

  public async execute(params: T): Promise<T> {
    const currentTime = Date.now();
    // Check if we need to use local storage (and prolong value)
    const predicateValue = this.PREDICATE();

    // check if there's an existing record in localStorage
    const existing = await this.browser.localStorage.getItem(this.STORAGE_KEY);
    if (existing) {
      try {
        const data = JSON.parse(existing);
        // if stored object has an expiry timestamp and is not expired yet
        if (data.expiry && currentTime < data.expiry) {
          if (predicateValue) {
            // Same campaign "Y" seen again -> extend the expiration by 3 days
            data.expiry = currentTime + this.FT_3DA_STORAGE_LIFETIME * 60000;
            await this.browser.localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
          }
          // whether extended or not, do not overwrite existing values before expiry
          return data;
        } else {
          // expired or malformed data: clean up
          await this.browser.localStorage.removeItem(this.STORAGE_KEY);
        }
      } catch {
        // if parse fails, remove corrupted data
        await this.browser.localStorage.removeItem(this.STORAGE_KEY);
      }
    }

    // at this point, no valid unexpired values exist in local storage

    if (predicateValue) {
      // the 3 Days Attribution triggered: store in localStorage with expiration
      const record = { ...params, expiry: currentTime + this.FT_3DA_STORAGE_LIFETIME * 60000 };
      await this.browser.localStorage.setItem(this.STORAGE_KEY, JSON.stringify(record));
      // clear session storage to avoid having duplicate data in two places
      await this.browser.sessionStorage.removeItem(this.STORAGE_KEY);
      return record;
    } else {
      // not a "borderfree" source (or feature 3 Days Attribution not triggered) -> fall back to session storage
      // only store if not already stored (avoid overwriting existing session values)
      const existingvalues = await this.browser.sessionStorage.getItem(this.STORAGE_KEY);
      if (existingvalues === null) {
        await this.browser.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(params));
        return params;
      } else {
        // if sessionStorage already has values, do nothing (preserve original values)
        try {
          return JSON.parse(existingvalues) as T;
        } catch {
          // if parse fails, remove corrupted data
          await this.browser.sessionStorage.removeItem(this.STORAGE_KEY);
          return null;
        }
      }
    }
  }
}
